import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useConnection } from './connection';

export interface ConsensusStatus {
  latestBlock: number | null;
  latestBlockTime: string | null;
  /** The app hash in the latest block's header: the chain's state root. */
  latestAppHash: string | null;
  latencyMs: number | null;
  error: string | null;
  loading: boolean;
  /** True once the first fetch has completed (success or error). */
  hasLoaded: boolean;
  refresh: () => void;
}

interface ConsensusCtx extends ConsensusStatus {
  /** Counts a reader in; the returned function counts it out. */
  subscribe: () => () => void;
}

const Ctx = createContext<ConsensusCtx | null>(null);

const POLL_MS = 15_000;
/** A /status with no answer by now counts as failed, so a page never waits on it forever. */
const FETCH_TIMEOUT_MS = 10_000;

// Owns the single shared /status fetch against the CometBFT RPC.
// HomePanel and ConnectionBar (and any other consumer) read from the
// same source instead of issuing parallel duplicates on page mount —
// the duplicate burst was producing 15s+ hangs when AWS ENA dropped
// SYNs from Cloudflare under load. It fetches and polls only while a page
// that reads it is open (the explorer pages), not on the account pages.
export function ConsensusStatusProvider({ children }: { children: ReactNode }) {
  const { consensusUrl } = useConnection();
  const [latestBlock, setLatestBlock] = useState<number | null>(null);
  const [latestBlockTime, setLatestBlockTime] = useState<string | null>(null);
  const [latestAppHash, setLatestAppHash] = useState<string | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const genRef = useRef(0);
  const [readers, setReaders] = useState(0);
  const active = readers > 0;

  const subscribe = useCallback(() => {
    setReaders((n) => n + 1);
    return () => setReaders((n) => n - 1);
  }, []);

  const fetchStatus = useCallback(async () => {
    const myGen = ++genRef.current;
    setLoading(true);
    const started = performance.now();
    try {
      const res = await fetch(`${consensusUrl}/status`, {
        signal: AbortSignal.timeout?.(FETCH_TIMEOUT_MS),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (genRef.current !== myGen) return;
      const sync = body?.result?.sync_info ?? body?.sync_info;
      const heightStr = sync?.latest_block_height;
      const ts = sync?.latest_block_time;
      const appHash = sync?.latest_app_hash;
      setLatestBlock(
        typeof heightStr === 'string' || typeof heightStr === 'number'
          ? Number(heightStr) || null
          : null,
      );
      setLatestBlockTime(typeof ts === 'string' ? ts : null);
      setLatestAppHash(typeof appHash === 'string' && appHash ? appHash.toLowerCase() : null);
      setLatencyMs(Math.round(performance.now() - started));
      setError(null);
    } catch (err) {
      if (genRef.current !== myGen) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (genRef.current === myGen) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  }, [consensusUrl]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    fetchStatus();
    const interval = setInterval(() => {
      if (!cancelled && !document.hidden) fetchStatus();
    }, POLL_MS);
    const onVis = () => {
      if (!cancelled && !document.hidden) fetchStatus();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [active, fetchStatus]);

  const value = useMemo<ConsensusCtx>(
    () => ({
      latestBlock,
      latestBlockTime,
      latestAppHash,
      latencyMs,
      error,
      loading,
      hasLoaded,
      refresh: fetchStatus,
      subscribe,
    }),
    [
      latestBlock,
      latestBlockTime,
      latestAppHash,
      latencyMs,
      error,
      loading,
      hasLoaded,
      fetchStatus,
      subscribe,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The shared consensus status; while any component using it is mounted, the
 *  provider fetches and polls. */
export function useConsensusStatus(): ConsensusStatus {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useConsensusStatus must be used within ConsensusStatusProvider');
  }
  const { subscribe } = ctx;
  useEffect(() => subscribe(), [subscribe]);
  return ctx;
}
